import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Icon from './AppIcon';
import cn from 'classnames';
import { ChevronDown, Home, Users, Settings, Bell, Shield, BarChart3, CreditCard, UserCog } from 'lucide-react';

const NavigationSidebar = ({ isCollapsed = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [agentsExpanded, setAgentsExpanded] = useState(true);
  const [profileExpanded, setProfileExpanded] = useState(false);
  const [billingExpanded, setBillingExpanded] = useState(false);
  const [isAgentsFolderOpen, setIsAgentsFolderOpen] = useState(false);
  const [isProfileFolderOpen, setIsProfileFolderOpen] = useState(false);

  const agentItems = [
    { icon: 'Users', label: 'Manager Agent', path: '/manager-agent-interface' },
    { icon: 'TrendingUp', label: 'Marketing Agent', path: '/marketing-agent-interface' },
    { icon: 'CheckSquare', label: 'Productivity Agent', path: '/personal-productivity-agent-interface' },
    { icon: 'BarChart3', label: 'Feedback Agent', path: '/feedback-analytics-agent-interface' },
    { icon: 'Sparkles', label: 'Onboarding Agent', path: '/onboarding-agent-interface' },
  ];

  const profileItems = [
    { icon: 'BarChart2', label: 'Analytics', path: '/analytics-dashboard' },
    { icon: 'Bell', label: 'Notifications', path: '/notification-preferences' },
    { icon: 'AlertTriangle', label: 'Alerts', path: '/alert-management' },
    { icon: 'UserCog', label: 'Admin', path: '/admin-dashboard' },
  ];

  const billingItems = [
    { icon: 'DollarSign', label: 'Pricing', path: '/pricing' },
    { icon: 'CreditCard', label: 'Billing', path: '/billing-management' },
    { icon: 'Settings', label: 'Tier Management', path: '/agent-management' },
  ];

  const mainMenuItems = [
    { icon: 'LayoutDashboard', label: 'Dashboard', path: '/dashboard' },
  ];

  const secondaryMenuItems = [
    { icon: 'Shield', label: 'Security', path: '/security-settings' },
    { icon: 'Lock', label: 'Data Privacy', path: '/data-privacy-settings' },
    { icon: 'Users', label: 'Team', path: '/team-management' },
    { icon: 'Lock', label: 'SSO Settings', path: '/sso-settings' },
  ];

  // Only show login/register if user is NOT authenticated
  const authMenuItems = !user ? [
    { icon: 'LogIn', label: 'Login', path: '/login' },
    { icon: 'UserPlus', label: 'Register', path: '/register' },
  ] : [];

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileOpen(false);
  };

  const renderNavItem = (item, isNested = false) => {
    const isActive = location?.pathname === item?.path;

    return (
      <div
        key={item?.path}
        onClick={() => handleNavigation(item?.path)}
        className={cn(
          'nav-item group relative transition-all duration-200',
          {
            'active shadow-lg bg-primary/10': isActive,
            'hover:glass-subtle hover:bg-muted/50': !isActive,
            'pl-8': isNested && !isCollapsed,
            'pl-3': !isNested || isCollapsed,
          }
        )}
        title={isCollapsed ? item?.label : ''}
      >
        {isActive && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-primary to-purple-600 rounded-r-full" />
        )}
        <div className="nav-item-icon">
          <Icon name={item?.icon} size={20} />
        </div>
        {!isCollapsed && (
          <span className="nav-item-label">{item?.label}</span>
        )}
      </div>
    );
  };

  const renderFolderItem = (title, icon, items, isExpanded, setIsExpanded) => {
    const hasActiveChild = items?.some(item => location?.pathname === item?.path);

    return (
      <div className="space-y-1">
        {/* Folder Header */}
        <div
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            'nav-item group relative cursor-pointer transition-all duration-300 ease-in-out',
            {
              'bg-primary/5': hasActiveChild,
              'hover:glass-subtle hover:bg-muted/50': !hasActiveChild,
            }
          )}
          title={isCollapsed ? title : ''}
        >
          <div className="nav-item-icon">
            <Icon name={icon} size={20} />
          </div>
          {!isCollapsed && (
            <>
              <span className="nav-item-label font-semibold">{title}</span>
              <div className={cn(
                "ml-auto transition-transform duration-300 ease-in-out",
                { 'rotate-180': isExpanded }
              )}>
                <ChevronDown size={16} className="text-muted-foreground" />
              </div>
            </>
          )}
        </div>

        {/* Folder Contents with Smooth Animation */}
        {!isCollapsed && (
          <div
            className={cn(
              'overflow-hidden transition-all duration-300 ease-in-out',
              {
                'max-h-0 opacity-0 scale-95': !isExpanded,
                'max-h-[500px] opacity-100 scale-100': isExpanded,
              }
            )}
          >
            <div className="space-y-1 py-1">
              {items?.map((item) => renderNavItem(item, true))}
            </div>
          </div>
        )}

        {/* Collapsed State - Show on Hover */}
        {isCollapsed && (
          <div className="hidden group-hover:block absolute left-full top-0 ml-2 z-50">
            <div className="glass-strong rounded-lg shadow-xl border border-border/50 p-2 min-w-[200px]">
              <div className="text-xs font-semibold text-muted-foreground px-3 py-2">
                {title}
              </div>
              <div className="space-y-1">
                {items?.map((item) => (
                  <div
                    key={item?.path}
                    onClick={() => handleNavigation(item?.path)}
                    className={cn(
                      'px-3 py-2 rounded-lg cursor-pointer transition-colors duration-200 flex items-center gap-3',
                      {
                        'bg-primary/10 text-primary': location?.pathname === item?.path,
                        'hover:bg-muted/50': location?.pathname !== item?.path,
                      }
                    )}
                  >
                    <Icon name={item?.icon} size={18} />
                    <span className="text-sm">{item?.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const navigationItems = [
    { name: 'Dashboard', path: '/dashboard', icon: Home },
    {
      name: 'Agents',
      icon: Users,
      isFolder: true,
      isOpen: isAgentsFolderOpen,
      toggle: () => setIsAgentsFolderOpen(!isAgentsFolderOpen),
      children: [
        { name: 'Manager Agent', path: '/manager-agent-interface' },
        { name: 'Marketing Agent', path: '/marketing-agent-interface' },
        { name: 'Productivity Agent', path: '/personal-productivity-agent-interface' },
        { name: 'Feedback Agent', path: '/feedback-analytics-agent-interface' },
        { name: 'Onboarding Agent', path: '/onboarding-agent-interface' },
      ],
    },
    {
      name: 'Billing',
      icon: CreditCard,
      isFolder: true,
      isOpen: billingExpanded,
      toggle: () => setBillingExpanded(!billingExpanded),
      children: [
        { name: 'Pricing', path: '/pricing' },
        { name: 'Billing', path: '/billing-management' },
        { name: 'Tier Management', path: '/agent-management' },
      ],
    },
    {
      name: 'Profile',
      icon: UserCog,
      isFolder: true,
      isOpen: isProfileFolderOpen,
      toggle: () => setIsProfileFolderOpen(!isProfileFolderOpen),
      children: [
        { name: 'Analytics', path: '/analytics-dashboard' },
        { name: 'Notifications', path: '/notification-preferences' },
        { name: 'Alerts', path: '/alert-management' },
        { name: 'Admin', path: '/admin-dashboard' },
        { name: 'Agent Management', path: '/agent-management' },
        { name: 'API Monitoring', path: '/api-monitoring-dashboard' },
      ],
    },
    { name: 'Team', path: '/team-management', icon: Users },
    { name: 'SSO Settings', path: '/sso-settings', icon: Shield },
    { name: 'Security', path: '/security-settings', icon: Shield },
    { name: 'Privacy', path: '/data-privacy-settings', icon: Shield },
  ];

  return (
    <>
      {/* Mobile Menu Button with Glass Effect */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="mobile-menu-button glass-strong hover:shadow-glass transition-all duration-300"
        aria-label="Toggle navigation menu"
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={24} />
      </button>

      {/* Mobile Overlay with Blur */}
      {isMobileOpen && (
        <div
          className="mobile-nav-overlay backdrop-blur-sm bg-background/80"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar with Glass Effect */}
      <aside
        className={cn(
          'fixed top-0 left-0 h-screen glass-strong border-r border-border/50 z-50 transition-all duration-300 shadow-glass-lg backdrop-blur-xl',
          {
            'w-64': !isCollapsed,
            'w-20': isCollapsed,
            'translate-x-0': isMobileOpen,
            '-translate-x-full lg:translate-x-0': !isMobileOpen,
          }
        )}
      >
        {/* Logo Section with Gradient */}
        <div className="sidebar-header border-b border-border/50">
          <div className="flex items-center justify-center gap-3">
            <div className="sidebar-logo bg-gradient-to-br from-primary/20 to-primary/10 shadow-glass overflow-hidden">
              <img 
                src="/assets/images/Photo_Feb_21_2026_12_00_04_AM_1_-1771901216615.png" 
                alt="DraftIQ Logo" 
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>

        {/* Navigation Items with Modern Styling */}
        <nav className="flex-1 overflow-y-auto py-6 px-3 custom-scrollbar">
          <div className="space-y-2">
            {/* Main Menu Items */}
            {mainMenuItems?.map((item) => renderNavItem(item))}

            {/* Agents Folder */}
            {renderFolderItem('Agents', 'Bot', agentItems, agentsExpanded, setAgentsExpanded)}

            {/* Billing Folder */}
            {renderFolderItem('Billing', 'CreditCard', billingItems, billingExpanded, setBillingExpanded)}

            {/* Secondary Menu Items */}
            {secondaryMenuItems?.map((item) => renderNavItem(item))}

            {/* Profile Folder */}
            {renderFolderItem('Profile', 'User', profileItems, profileExpanded, setProfileExpanded)}

            {/* Auth Items (Login/Register) - Only for non-authenticated users */}
            {authMenuItems?.map((item) => renderNavItem(item))}
          </div>
        </nav>

        {/* Footer with Glass Effect */}
        {user && (
          <div className="border-t border-border/50 p-4">
            <div className="glass-subtle rounded-xl p-3 hover-lift cursor-pointer transition-smooth">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center shadow-sm">
                  <Icon name="User" size={20} color="var(--color-primary)" />
                </div>
                {!isCollapsed && (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">User Account</p>
                    <p className="text-xs text-muted-foreground truncate">View Profile</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default NavigationSidebar;